import type { RepresentationSummary } from '../MyDashboard/service';
import { DataSanitizationUtils } from '@/chassis/core/utils/datasanitization-utils';
import { publicAgencyRepresentationTypeI18nOptions } from '@/domains/pages/3.3-registration-module-representation/constants';

export type RepresentationEntityType = 'supplier' | 'public_agency';

const ENTITY_RESOURCE_PREFIX: Record<RepresentationEntityType, string> = {
    supplier: 'SUPPLIER',
    public_agency: 'PUBLICAGENCY',
};

const SUPPLIER_PREFIXES = new Set(['SUPPLIER']);
const PUBLIC_AGENCY_PREFIXES = new Set(['PUBLICAGENCY', 'AGENCY', 'PUBLICAG']);
const PUBLIC_AGENCY_REPRESENTATION_TYPES = new Set(publicAgencyRepresentationTypeI18nOptions.map((option) => option.value));

export const buildEntityCacheKey = (type: RepresentationEntityType, id: string) => `${type}:${id}`;

export const normalizeResourceId = (prefix: string, value?: string | null): string | null => {
    const normalized = DataSanitizationUtils.normalizeResourceIdValue(value);
    if (!normalized) {
        return null;
    }

    const coreId = DataSanitizationUtils.splitResourceId(normalized)?.remainder ?? normalized;
    return DataSanitizationUtils.ensureResourcePrefix(prefix, coreId);
};

export const sanitizeEntityIdForType = (type: RepresentationEntityType, value?: string | null): string | null => {
    const parts = DataSanitizationUtils.splitResourceId(value);
    if (!parts) {
        return null;
    }

    const { prefix, remainder } = parts;
    if (!prefix) {
        return remainder;
    }

    const upper = prefix.toUpperCase();

    if (type === 'supplier' && SUPPLIER_PREFIXES.has(upper)) {
        return remainder;
    }

    if (type === 'public_agency' && PUBLIC_AGENCY_PREFIXES.has(upper)) {
        return remainder;
    }

    return remainder;
};

export const resolveRepresentationEntity = (
    summary: RepresentationSummary,
): { type: RepresentationEntityType; id: string } | null => {
    const prefersPublicAgency = typeof summary.representationType === 'string' && PUBLIC_AGENCY_REPRESENTATION_TYPES.has(summary.representationType);
    const publicAgencyId = sanitizeEntityIdForType('public_agency', summary.entityPublicAgencyId);
    const supplierId = sanitizeEntityIdForType('supplier', summary.entitySupplierId);

    if (prefersPublicAgency && publicAgencyId) {
        return {
            type: 'public_agency',
            id: publicAgencyId,
        };
    }

    if (supplierId) {
        return {
            type: 'supplier',
            id: supplierId,
        };
    }

    if (publicAgencyId) {
        return {
            type: 'public_agency',
            id: publicAgencyId,
        };
    }

    return null;
};
